import React, { useRef, useEffect } from 'react'
import usePlayStore from '../../stores/playStore'

const Timeline = () => {
  const timelineRef = useRef(null)
  const { 
    currentTime, 
    duration, 
    setCurrentTime, 
    isPlaying,
    animations,
    teams,
    props
  } = usePlayStore()

  useEffect(() => {
    if (isPlaying) {
      console.log('Starting timeline playback')
      const interval = setInterval(() => {
        const time = typeof currentTime === 'number' ? currentTime : 0
        const newTime = time >= duration ? 0 : time + 0.1
        if (newTime % 1 === 0) { // Log every second
          console.log('Timeline time:', newTime)
        }
        setCurrentTime(newTime)
      }, 100)
      
      return () => clearInterval(interval)
    } else {
      console.log('Stopping timeline playback')
    }
  }, [isPlaying, duration, setCurrentTime, currentTime])

  const handleTimelineClick = (e) => {
    if (!timelineRef.current) return
    
    const rect = timelineRef.current.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const percentage = clickX / rect.width
    const newTime = percentage * duration
    
    setCurrentTime(newTime)
  }

  const getTimePosition = () => {
    const time = typeof currentTime === 'number' ? currentTime : 0
    return (time / duration) * 100
  }

  // Group animations by entity (team or prop)
  const groupedAnimations = animations.reduce((groups, animation) => {
    const entityId = animation.teamId || animation.propId
    if (!groups[entityId]) {
      groups[entityId] = []
    }
    groups[entityId].push(animation)
    return groups
  }, {})

  const entityIds = Object.keys(groupedAnimations)

  // Get the color for an entity (team or prop)
  const getEntityColor = (entityId) => {
    // Check if it's a team
    const team = teams.find(t => t.id === entityId)
    if (team) {
      return team.color
    }
    
    // Check if it's a prop
    const prop = props.find(p => p.id === entityId)
    if (prop) {
      return prop.color
    }
    
    // Default color if not found
    return '#3B82F6' // blue
  }

  // Get the name for an entity (team or prop)
  const getEntityName = (entityId) => {
    // Check if it's a team
    const team = teams.find(t => t.id === entityId)
    if (team) {
      return `${team.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} #${team.number}`
    }
    
    // Check if it's a prop
    const prop = props.find(p => p.id === entityId)
    if (prop) {
      if (prop.type === 'soccer-ball') {
        return 'Soccer Ball'
      } else if (prop.type === 'cone') {
        return 'Cone'
      } else {
        return prop.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())
      }
    }
    
    // Default name if not found
    return 'Unknown Entity'
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-900">Timeline</h4>
        <div className="text-xs text-gray-500">
          {Math.floor(typeof currentTime === 'number' ? currentTime : 0)}s / {Math.floor(duration)}s
        </div>
      </div>
      
      <div className="relative">
        {/* Timeline Track */}
        <div
          ref={timelineRef}
          className="w-full bg-gray-200 rounded-lg cursor-pointer relative overflow-hidden"
          style={{ height: `${Math.max(32, entityIds.length * 12 + 8)}px` }}
          onClick={handleTimelineClick}
        >
          {/* Animation Tracks - One row per entity */}
          {entityIds.map((entityId, rowIndex) => {
            const entityAnimations = groupedAnimations[entityId]
            const entityColor = getEntityColor(entityId)
            const entityName = getEntityName(entityId)
            return entityAnimations.map((animation, animIndex) => (
              <div
                key={animation.id}
                className="absolute rounded"
                style={{
                  left: `${(animation.startTime / duration) * 100}%`,
                  width: `${((animation.endTime - animation.startTime) / duration) * 100}%`,
                  top: `${rowIndex * 12 + 2}px`,
                  height: '8px',
                  backgroundColor: entityColor
                }}
                title={`${entityName}: ${animation.startTime}s - ${animation.endTime}s`}
              />
            ))
          })}
          
          {/* Current Time Indicator */}
          <div
            className="absolute top-0 w-1 h-full bg-red-500 pointer-events-none"
            style={{ left: `${getTimePosition()}%` }}
          />
          
          {/* Time Markers */}
          {Array.from({ length: Math.floor(duration) + 1 }, (_, i) => (
            <div
              key={i}
              className="absolute top-0 w-px h-full bg-gray-400 pointer-events-none"
              style={{ left: `${(i / duration) * 100}%` }}
            />
          ))}
        </div>
        
        {/* Time Labels */}
        <div className="flex justify-between mt-1 text-xs text-gray-500">
          <span>0s</span>
          <span>{Math.floor(duration / 2)}s</span>
          <span>{Math.floor(duration)}s</span>
        </div>
      </div>
      
      {/* Animation Info */}
      <div className="mt-4">
        <div className="text-sm text-gray-600">
          {entityIds.length} entit{entityIds.length !== 1 ? 'ies' : 'y'} with {animations.length} animation{animations.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  )
}

export default Timeline
